/**
 * OfacScanSection — per-person OFAC sanctions screen.
 *
 * Bill 2026-05-08 — relocated from the retired cart-level ComplianceStep
 * into PersonModal. Each person's modal hosts its own OFAC scan + status
 * pill (was hardcoded to primary-only in the SPFx implementation).
 *
 * Bill 2026-05-09 — OFAC archive-match suppression. Two-step flow:
 *
 *   1. Call JHA `client.jxrl.ofacSrch` (existing; unchanged wire shape).
 *   2. If JHA returned hits, call `client.retail.ofac.classifyHits(aliasIds)`
 *      to ask our local SDN cache whether each AliasId is on Treasury's
 *      CURRENT list or only on archived/delisted snapshots.
 *
 * The rollup `OfacStatus` then splits into three terminal buckets:
 *   - `clear`         — 0 hits.
 *   - `archived-only` — ≥1 hit, ALL classified as historical → allow.
 *   - `hit-current`   — ≥1 hit, ≥1 still on the active SDN list → block.
 *
 * `unknown` from the classifier (cache unreachable) maps to `current` so
 * a transient cache outage never silently allows a sanction through.
 *
 * Wires:
 *   - apiClient.jxrl.ofacSrch({ ComName, ComNameType: 'Indv' })
 *   - apiClient.retail.ofac.classifyHits([aliasId, ...])
 *   - Persists rolled-up status into `person.extra.ofacStatus`
 *   - Persists per-match classifications into `person.extra.ofacClassifications`
 *   - Renders a 3-state status pill + state-specific banner copy
 */
import { useState, useMemo } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import type { RaoPerson } from '../../../types';
import { useEmitEvent } from '../../../hooks/useEmitEvent';
import {
  normalizeOfacStatus,
  type OfacClassificationRecord,
  type OfacStatus,
} from './ofacStatus';
import type { AttachedDoc } from './attachedDocs';
import { OfacOverrideModal, type OfacOverrideSubmission } from './OfacOverrideModal';
import {
  auditEventForClearance,
  fsrOverride,
  ofacStateCOverrideEvent,
  upsertSection,
  type SectionClearance,
  type VerifyClearanceMap,
} from './verifyClearance';
// Headless scan core — SHARED with the PdwShell save-time auto-fire
// (Bill 2026-07-23). Wire shape, classification rollup, and the persisted
// extra.* patch all live in ofacAutoScan.ts so the two paths can't drift.
import {
  buildOfacExtraPatch,
  buildOfacScreenName,
  performOfacScan,
  type OfacMatch,
  type OfacScreeningStamp,
} from './ofacAutoScan';

/** Plain names for the bank's list sources (SanctionsSources.cs ids). */
const SOURCE_SHORT: Record<string, string> = {
  'ofac-sdn': 'OFAC SDN list',
  'ofac-cons': 'OFAC consolidated list',
  'us-csl': 'U.S. Commerce / State list',
  un: 'United Nations list',
  eu: 'European Union list',
  uk: 'UK Sanctions List',
  ca: 'Canada sanctions list',
  au: 'Australia sanctions list',
};

const FORECAST_LABEL: Record<string, string> = {
  'also-our-match': 'Same party our screening found',
  delisted: 'Expected false positive: delisted by Treasury',
  'not-our-match': 'Expected false positive: not a match under our rules',
  unaccounted: 'Not on our lists: sent to Risk',
};

function readScreening(person: RaoPerson): OfacScreeningStamp | undefined {
  const extra = (person as unknown as { extra?: { ofacScreening?: unknown } }).extra;
  const raw = extra?.ofacScreening;
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Partial<OfacScreeningStamp>;
  if (typeof r.screeningId !== 'string') return undefined;
  return {
    screeningId: r.screeningId,
    verdict: typeof r.verdict === 'string' ? r.verdict : '',
    authority: typeof r.authority === 'string' ? r.authority : 'pcb',
    topScore: typeof r.topScore === 'number' ? r.topScore : 0,
    summary: typeof r.summary === 'string' ? r.summary : '',
    matcherVersion: typeof r.matcherVersion === 'string' ? r.matcherVersion : '',
    candidates: Array.isArray(r.candidates) ? r.candidates : [],
    forecast: Array.isArray(r.forecast) ? r.forecast : [],
  };
}

function readMatches(person: RaoPerson): OfacMatch[] {
  const extra = (person as unknown as { extra?: { ofacMatches?: unknown } }).extra;
  const raw = extra?.ofacMatches;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map((r) => ({
      matchType: typeof r.matchType === 'string' ? r.matchType : '',
      aliasName: typeof r.aliasName === 'string' ? r.aliasName : '',
      aliasId: typeof r.aliasId === 'string' ? r.aliasId : '',
      matchRef: typeof r.matchRef === 'string' ? r.matchRef : '',
    }));
}

function readClassifications(person: RaoPerson): OfacClassificationRecord[] {
  const extra = (person as unknown as { extra?: { ofacClassifications?: unknown } }).extra;
  const raw = extra?.ofacClassifications;
  if (!Array.isArray(raw)) return [];
  const out: OfacClassificationRecord[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const row = r as Record<string, unknown>;
    const cls = typeof row.classification === 'string' ? row.classification : 'unknown';
    const classification: 'current' | 'archived-only' | 'unknown' =
      cls === 'current' || cls === 'archived-only' ? cls : 'unknown';
    const programsRaw = row.programs;
    const programs: string[] = Array.isArray(programsRaw)
      ? programsRaw.filter((p): p is string => typeof p === 'string')
      : [];
    out.push({
      aliasId: typeof row.aliasId === 'string' ? row.aliasId : String(row.aliasId ?? ''),
      classification,
      isCurrentSdn: row.isCurrentSdn === true,
      entityId: typeof row.entityId === 'string' ? row.entityId : null,
      primaryName: typeof row.primaryName === 'string' ? row.primaryName : null,
      programs,
      akaName: typeof row.akaName === 'string' ? row.akaName : null,
      akaType: typeof row.akaType === 'string' ? row.akaType : null,
    });
  }
  return out;
}

/**
 * Plain-language label for the JHA `OFACMatchType` enum value. Per
 * `feedback_plain_language_assistive_ux_20260508.md` — never surface
 * raw enum codes to the FSR.
 */
function matchTypeLabel(raw: string): string {
  switch (raw) {
    case 'OFACPrimary': return 'Primary name match';
    case 'OFACAlias':   return 'Alias / AKA match';
    case 'OFACAddress': return 'Address match';
    case 'OFACDOB':     return 'Date-of-birth match';
    case 'OFACID':      return 'ID number match';
    default: return raw || 'Match';
  }
}

export interface OfacScanSectionProps {
  person: RaoPerson;
  /**
   * The application this screening belongs to. Recorded with the screening so the OFAC result can be traced to
   * the application it cleared (and printed in the LexisNexis report PDF). 20 of 24 person-dialog screenings
   * through 11/2 had no application on record because this section never sent it.
   */
  cartId?: string;
  onChange: (patch: Partial<RaoPerson>) => void;
  readOnly?: boolean;
  /**
   * 'Indv' for natural persons (Individual / NRA / Joint / Minor /
   * Conservator / Rep Payee / VAFid / UTMA / Estate-PR), 'Org' for
   * Business / Trust / NonProfit / etc. Drives the ComNameType wire
   * value on OFACSrch.
   */
  comNameType?: 'Indv' | 'Org';
  /**
   * Dialog-level attached documents — surfaced to the State C override
   * modal so the FSR can pick a compensating doc. Bill 2026-05-21 §7.5.
   */
  attachedDocs?: ReadonlyArray<AttachedDoc>;
}

function readStatus(person: RaoPerson): OfacStatus {
  const extra = (person as unknown as { extra?: { ofacStatus?: string } }).extra;
  return normalizeOfacStatus(extra?.ofacStatus);
}

/**
 * Read the persisted `verifyClearance.ofac` row (if any). Authoritative for
 * the FSR override state; falls through to the raw OfacStatus pill when
 * absent. See `verifyClearance.ts` §7.4.2 / §7.5.
 */
function readOfacClearance(person: RaoPerson): SectionClearance | undefined {
  const extra = (person as unknown as { extra?: Record<string, unknown> }).extra;
  const map = extra?.['verifyClearance'] as VerifyClearanceMap | undefined;
  return map?.ofac;
}

/** Risk Management's clear / confirm decision, stamped server-side by SanctionsReviewService (Bill 2026-11-02). */
interface OfacRiskDecision {
  decision: 'cleared' | 'confirmed' | 'escalated';
  /** officer (decided in this app) | risk (Risk panel). */
  as?: string;
  screeningId?: string;
  by?: string;
  at?: string;
  reason?: string;
}

function readRiskDecision(person: RaoPerson): OfacRiskDecision | undefined {
  const raw = (person as unknown as { extra?: { ofacRiskDecision?: unknown } }).extra?.ofacRiskDecision;
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Partial<OfacRiskDecision>;
  return r.decision === 'cleared' || r.decision === 'confirmed' || r.decision === 'escalated' ? (r as OfacRiskDecision) : undefined;
}

function statusPill(status: OfacStatus, verdict?: string): JSX.Element {
  if (status === 'clear') return <Pill variant="ok">OFAC Clear</Pill>;
  if (status === 'hit-current' && verdict === 'review') return <Pill variant="err">Possible match — Risk review</Pill>;
  if (status === 'hit-current') return <Pill variant="err">OFAC Hit — Current sanction</Pill>;
  if (status === 'archived-only') return <Pill variant="warn">OFAC Match — Historical</Pill>;
  if (status === 'pending') return <Pill variant="warn">OFAC pending</Pill>;
  return <Pill variant="muted">OFAC not run</Pill>;
}

/** Per-match classification chip — color-coded current vs. historical. */
function classificationChip(
  cls: OfacClassificationRecord | undefined,
): JSX.Element | null {
  if (!cls) return null;
  if (cls.classification === 'current' || cls.classification === 'unknown') {
    return (
      <Pill variant="err">
        Current SDN{cls.programs.length > 0 ? ` · ${cls.programs.join(' / ')}` : ''}
      </Pill>
    );
  }
  if (cls.classification === 'archived-only') {
    return <Pill variant="warn">Historical / Delisted</Pill>;
  }
  return null;
}

export function OfacScanSection({
  person,
  cartId,
  onChange,
  readOnly,
  comNameType = 'Indv',
  attachedDocs = [],
}: OfacScanSectionProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const emit = useEmitEvent();
  const [running, setRunning] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [freshMatches, setFreshMatches] = useState<OfacMatch[] | null>(null);
  const [freshClassifications, setFreshClassifications] = useState<
    OfacClassificationRecord[] | null
  >(null);
  const [freshScreening, setFreshScreening] = useState<OfacScreeningStamp | null>(null);
  const [pdfBusy, setPdfBusy] = useState<boolean>(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  // Bill 2026-05-21 §7.5 — OFAC State C FSR-override surface state.
  const [overrideModalOpen, setOverrideModalOpen] = useState<boolean>(false);

  // BACKLOG-0517 (Bill 2026-09-17) — the FSR override is OFF during the pilot:
  // the officer could record one and the server still refused it at commit,
  // which is safe but confusing. Current practice stands: an active sanctions
  // match goes to Risk Management. Admin-configurable, no deploy needed
  // (SystemSettings 'risk.ofac.fsrOverride.enabled' = true brings it back).
  const { config } = useConfig();
  const fsrOverrideEnabled = useMemo(() => {
    const raw = (config.systemSettings as Record<string, unknown> | undefined)?.[
      'risk.ofac.fsrOverride.enabled'
    ];
    return raw === true || raw === 'true' || raw === 1 || raw === '1';
  }, [config.systemSettings]);

  const status = readStatus(person);
  const ofacClearance = readOfacClearance(person);
  const riskDecision = readRiskDecision(person);
  // Jessica Beucler 2026-11-02: the officer decides first and can send it to Risk (sanctions.review.decider;
  // defaults match the server's SanctionsReviewSettings: officer-first, officers may clear a Match).
  const settingsMap = config.systemSettings as Record<string, unknown> | undefined;
  const officerFirst = String(settingsMap?.['sanctions.review.decider'] ?? 'officer-first') !== 'risk';
  // Jessica 2026-11-02: a Match (92+) goes straight to Risk unless Risk turns this on (default off, as the server).
  const officerClearsMatches = String(settingsMap?.['sanctions.review.officerClearsMatches'] ?? 'false').toLowerCase() === 'true';
  const [decision, setDecision] = useState<'cleared' | 'confirmed' | 'escalated' | null>(null);
  const [reason, setReason] = useState<string>('');
  const [deciding, setDeciding] = useState<boolean>(false);
  const isOverridden = ofacClearance?.state === 'fsr-override';
  const fullName = buildOfacScreenName(person);
  const canRun = !running && !readOnly && fullName.length > 0;

  // Show in-flight scan results when the screen just ran; otherwise fall back
  // to whatever was persisted in extra.ofacMatches from the previous run so
  // the FSR sees the hits across modal re-opens / page reloads.
  const matches = useMemo<OfacMatch[]>(
    () => freshMatches ?? readMatches(person),
    [freshMatches, person],
  );

  const screening = useMemo<OfacScreeningStamp | undefined>(
    () => freshScreening ?? readScreening(person),
    [freshScreening, person],
  );

  // CHG-0141 (Bill 2026-11-02): the Sanctions Screening report for the latest screening, as a PDF.
  const downloadReport = async (screeningId: string): Promise<void> => {
    setPdfBusy(true);
    setPdfError(null);
    try {
      const blob = await client.retail.ofac.getScreeningReportPdf(screeningId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Sanctions Screening.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err: unknown) {
      setPdfError((err as Error)?.message ?? 'Could not print the report.');
    } finally {
      setPdfBusy(false);
    }
  };
  const forecastByAliasId = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    for (const f of screening?.forecast ?? []) if (f.aliasId) m.set(f.aliasId, f.assessment);
    return m;
  }, [screening]);

  const classifications = useMemo<OfacClassificationRecord[]>(
    () => freshClassifications ?? readClassifications(person),
    [freshClassifications, person],
  );

  // Index classifications by AliasId for per-card lookup.
  const classByAliasId = useMemo<Map<string, OfacClassificationRecord>>(() => {
    const m = new Map<string, OfacClassificationRecord>();
    for (const c of classifications) {
      if (c.aliasId) m.set(c.aliasId, c);
    }
    return m;
  }, [classifications]);

  // Persistence shape (ofacStatus/ofacScannedAt/matches/classifications +
  // verifyClearance.ofac mirror + override invalidation) lives in the shared
  // buildOfacExtraPatch — one writer for this path AND the save-time auto-fire.
  const persistResult = (
    nextStatus: OfacStatus,
    nextMatches: OfacMatch[],
    nextClassifications: OfacClassificationRecord[],
    nextScreening?: OfacScreeningStamp,
  ): void => {
    const extra = ((person as unknown as { extra?: Record<string, unknown> }).extra) ?? {};
    onChange({
      extra: buildOfacExtraPatch(extra, nextStatus, nextMatches, nextClassifications, nextScreening),
    } as unknown as Partial<RaoPerson>);
  };

  // The officer's decision on the held screening shown (officer-first mode).
  const submitDecision = async (screeningId: string): Promise<void> => {
    if (!decision) return;
    setDeciding(true);
    setError(null);
    try {
      const res = await client.retail.ofac.decideScreening(screeningId, decision, reason.trim());
      if (!res.success || !res.decision) throw new Error(res.error ?? 'The decision was not saved.');
      const extra = ((person as unknown as { extra?: Record<string, unknown> }).extra) ?? {};
      const patch = decision === 'cleared'
        ? buildOfacExtraPatch(extra, 'clear', matches, classifications, screening)
        : { ...extra };
      (patch as Record<string, unknown>)['ofacRiskDecision'] = {
        decision, as: 'officer', screeningId, by: res.decision.decidedBy, at: res.decision.decidedAt, reason: res.decision.reason,
        decisionId: res.decision.decisionId,
      };
      onChange({ extra: patch } as unknown as Partial<RaoPerson>);
      setDecision(null);
      setReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The decision was not saved.');
    } finally {
      setDeciding(false);
    }
  };

  const submitOverride = (submission: OfacOverrideSubmission): void => {
    const extra = ((person as unknown as { extra?: Record<string, unknown> }).extra) ?? {};
    const officer = {
      officerCode: user?.officerCode,
      entraOid: user?.upn,
    };
    const clearance = fsrOverride(
      'ofac',
      submission.reason,
      submission.compDocId,
      officer,
    );
    const prevMap = extra['verifyClearance'] as VerifyClearanceMap | undefined;
    const nextExtra: Record<string, unknown> = {
      ...extra,
      verifyClearance: upsertSection(prevMap, clearance),
      ofacOverride: {
        reason: clearance.reason,
        compDocId: clearance.compDocId,
        compDocLabel: submission.compDocLabel,
        fsrOfficerCode: clearance.fsrOfficerCode ?? '',
        fsrEntraOid: clearance.fsrEntraOid ?? '',
        overriddenAt: clearance.clearedAt,
      },
    };
    onChange({ extra: nextExtra } as unknown as Partial<RaoPerson>);

    // Locked-name audit events — §7.4.2 (generic) + §7.5 (OFAC-specific).
    const general = auditEventForClearance(clearance);
    if (general) {
      emit({
        eventType: general.eventType,
        eventCategory: 'compliance',
        severity: 'warn',
        payload: general.payload as Record<string, unknown>,
      });
    }
    const ofacSpecific = ofacStateCOverrideEvent(clearance);
    if (ofacSpecific) {
      emit({
        eventType: ofacSpecific.eventType,
        eventCategory: 'compliance',
        severity: 'warn',
        payload: ofacSpecific.payload as Record<string, unknown>,
      });
    }
    setOverrideModalOpen(false);
  };

  const runScan = async (): Promise<void> => {
    setRunning(true);
    setError(null);
    setFreshMatches(null);
    setFreshClassifications(null);
    setFreshScreening(null);
    try {
      // Shared two-step core: JHA ofacSrch → local SDN-cache classifyHits
      // (classifier outage fail-safes to 'hit-current' inside the helper).
      const outcome = await performOfacScan(client, fullName, comNameType, {
        birthDate: person.dob || undefined,
        cartId: cartId || undefined,
        personKey: person.id || undefined,
      });
      setFreshMatches(outcome.matches);
      setFreshClassifications(outcome.records);
      setFreshScreening(outcome.screening ?? null);
      persistResult(outcome.status, outcome.matches, outcome.records, outcome.screening);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'OFAC screen failed.';
      setError(msg);
      persistResult('pending', [], []);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      style={{
        padding: '12px 14px',
        border: '1px solid var(--ink-200, var(--surface-border))',
        borderRadius: 6,
        background: 'var(--surface-card, #fff)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ flex: 1, fontSize: 13, color: 'var(--pcb-blue-dark)' }}>
          OFAC Sanctions Screening
        </strong>
        {statusPill(status, screening?.verdict)}
      </div>
      {fullName.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>
          Enter the customer&apos;s name on the Profile tab before running the OFAC screen.
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>
          Screening name: <strong>{fullName}</strong>
        </div>
      )}
      {error && <Banner tone="err">{error}</Banner>}
      {/* Risk Management's decision on the screening shown (Risk panel > Sanctions > Reviews). */}
      {riskDecision && (!screening || !riskDecision.screeningId || riskDecision.screeningId === screening.screeningId) && (
        <Banner tone={riskDecision.decision === 'cleared' ? 'ok' : riskDecision.decision === 'escalated' ? 'info' : 'err'}>
          {(() => {
            const who = riskDecision.as === 'officer' ? 'The officer' : 'Risk Management';
            const by = riskDecision.by ? ` (${riskDecision.by})` : '';
            if (riskDecision.decision === 'escalated') return `Sent to Risk Management${by}. The application waits for their decision.`;
            return riskDecision.decision === 'cleared'
              ? `${who} cleared this result as not a match${by}. The application can continue.`
              : `${who} confirmed a sanctions match${by}. The application cannot continue.`;
          })()}
        </Banner>
      )}
      {/* Officer first (Jessica 2026-11-02): decide here, or send it to Risk Management. */}
      {status === 'hit-current' && officerFirst && screening && screening.verdict === 'hit' && !officerClearsMatches
        && !(riskDecision && riskDecision.screeningId === screening.screeningId) && (
        <Banner tone="info">
          This is a Match (92 or more on an OFAC list), so it has gone straight to Risk Management for a second review.
          The application waits for their decision.
        </Banner>
      )}
      {status === 'hit-current' && officerFirst && !readOnly && screening
        && !(screening.verdict === 'hit' && !officerClearsMatches)
        && !(riskDecision && riskDecision.screeningId === screening.screeningId) && (
        <div style={{ border: '1px solid var(--banner-err-border, #b10505)', borderRadius: 6, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <strong style={{ fontSize: 13 }}>Your decision</strong>
          <div style={{ fontSize: 12 }}>
            Compare each listing below with the customer (other names, date of birth, nationality, ID numbers, address), as OFAC
            recommends. If you cannot tell, send it to Risk Management.
          </div>
          <label style={{ fontSize: 13 }}>
            <input type="radio" name={`ofac-d-${screening.screeningId}`} checked={decision === 'cleared'} onChange={() => setDecision('cleared')} />{' '}
            <strong>Clear</strong>: the customer is not the listed party.
          </label>
          <label style={{ fontSize: 13 }}>
            <input type="radio" name={`ofac-d-${screening.screeningId}`} checked={decision === 'confirmed'} onChange={() => setDecision('confirmed')} />{' '}
            <strong>Confirm match</strong>: the customer is the listed party. Stop; do not open.
          </label>
          <label style={{ fontSize: 13 }}>
            <input type="radio" name={`ofac-d-${screening.screeningId}`} checked={decision === 'escalated'} onChange={() => setDecision('escalated')} />{' '}
            <strong>Send to Risk Management</strong>: I cannot make a determination.
          </label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={2000}
            placeholder="What you compared and what you found (required)" style={{ fontSize: 13 }} />
          <div>
            <Button variant="primary" onClick={() => void submitDecision(screening.screeningId)}
              disabled={deciding || !decision || reason.trim().length < 10}>
              {deciding ? 'Saving…' : 'Record decision'}
            </Button>
          </div>
        </div>
      )}
      {/* State-specific banners — only one at a time, status-driven. */}
      {status === 'hit-current' && !isOverridden && (
        <Banner tone="err">
          {fsrOverrideEnabled
            ? 'Active OFAC sanctions match. Risk Management review required. The FSR may submit a false-positive override with a compensating document and reason (audit-logged); Risk Management is notified and the cart is no longer hard-blocked while review runs.'
            : 'Active OFAC sanctions match. Risk Management reviews this before the application goes any further — send it to them and stop here.'}
        </Banner>
      )}
      {/* Bill 2026-05-21 §7.5 — State C FSR-override surface. Hidden until
          a 'hit-current' lands; replaced by the override-caption banner once
          submitted. Re-running the scan clears the override (new hit-set
          requires a new override). */}
      {status === 'hit-current' && !isOverridden && !readOnly && fsrOverrideEnabled && (
        <div>
          <Button
            variant="ghost"
            onClick={() => setOverrideModalOpen(true)}
            disabled={running}
          >
            Override with compensating doc…
          </Button>
        </div>
      )}
      {isOverridden && ofacClearance && (
        <Banner tone="warn" title={`Overridden by ${ofacClearance.fsrOfficerCode ?? 'FSR'}`}>
          OFAC sanctions match marked as a false positive on{' '}
          {ofacClearance.clearedAt
            ? new Date(ofacClearance.clearedAt).toLocaleString()
            : 'unknown date'}
          . Reason: <em>{ofacClearance.reason}</em>. Compensating document
          attached (id: {ofacClearance.compDocId ?? 'n/a'}). Risk Management
          Adaptive Card dispatched.
        </Banner>
      )}
      {status === 'archived-only' && (
        <Banner tone="warn">
          JHA flagged this name against a historical OFAC record. Treasury removed the
          entry from the active sanctions list. Onboarding may continue. The match is
          logged in the audit trail and visible to Risk Management.
        </Banner>
      )}
      {/* The bank's own screening (Bill 2026-10-19): the listed parties our lists matched. */}
      {screening && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--ink-800, #2a2a2f)' }}>{screening.summary}</div>
            <Button variant="ghost" onClick={() => void downloadReport(screening.screeningId)} disabled={pdfBusy}>
              {pdfBusy ? 'Preparing PDF…' : 'Download report'}
            </Button>
          </div>
          {pdfError && <div style={{ fontSize: 12, color: 'var(--banner-err-border, #b10505)' }}>{pdfError}</div>}
          {screening.candidates.map((c) => (
            <div
              key={`${c.sourceId}-${c.sourceEntryId}`}
              style={{
                padding: '8px 12px',
                border: `1px solid ${c.band === 'hit' ? 'var(--banner-err-border, #b10505)' : 'var(--banner-warn-border, #d4a72c)'}`,
                background: c.band === 'hit' ? 'var(--banner-err-bg, #fdecec)' : 'var(--banner-warn-bg, #fff8e1)',
                borderRadius: 6,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Pill variant={c.band === 'hit' ? 'err' : 'warn'}>
                  {c.band === 'hit' ? 'Match' : c.band === 'review' ? 'Possible match' : 'Cleared by date or country'}
                  {` · score ${c.score}`}
                </Pill>
                <strong style={{ fontSize: 13, color: 'var(--ink-900, #1d1d1f)' }}>{c.matchedName}</strong>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-700, #4b5563)' }}>
                {SOURCE_SHORT[c.sourceId] ?? c.sourceId}
                {c.primaryName && c.primaryName !== c.matchedName ? ` · listed as ${c.primaryName}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Core Director's OFAC search: what its nightly report will show (a forecast, not the decision). */}
      {screening && matches.length > 0 && (
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-800, #2a2a2f)', marginTop: 4 }}>
          Core Director&apos;s nightly OFAC report will also list:
        </div>
      )}
      {/* Per-match cards — extended with classification chip + program code. */}
      {matches.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '4px 0',
          }}
        >
          {matches.map((m, idx) => {
            const cls = classByAliasId.get(m.aliasId);
            const isHistorical = cls?.classification === 'archived-only';
            const cardBorder = isHistorical
              ? 'var(--banner-warn-border, #d4a72c)'
              : 'var(--banner-err-border, #b10505)';
            const cardBg = isHistorical
              ? 'var(--banner-warn-bg, #fff8e1)'
              : 'var(--banner-err-bg, #fdecec)';
            return (
              <div
                key={`${m.aliasId}-${idx}`}
                style={{
                  padding: '10px 12px',
                  border: `1px solid ${cardBorder}`,
                  background: cardBg,
                  borderRadius: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Pill variant="warn">{matchTypeLabel(m.matchType)}</Pill>
                  {classificationChip(cls)}
                  {forecastByAliasId.get(m.aliasId) && (
                    <Pill variant={forecastByAliasId.get(m.aliasId) === 'unaccounted' || forecastByAliasId.get(m.aliasId) === 'also-our-match' ? 'err' : 'muted'}>
                      {FORECAST_LABEL[forecastByAliasId.get(m.aliasId) as string] ?? forecastByAliasId.get(m.aliasId)}
                    </Pill>
                  )}
                  <strong style={{ fontSize: 13, color: 'var(--ink-900, #1d1d1f)' }}>
                    {m.aliasName || '(no name returned)'}
                  </strong>
                </div>
                {cls?.primaryName && cls.primaryName !== m.aliasName && (
                  <div style={{ fontSize: 12, color: 'var(--ink-800, #2a2a2f)' }}>
                    SDN entry: <strong>{cls.primaryName}</strong>
                    {cls.akaName && cls.akaName !== cls.primaryName && (
                      <span style={{ color: 'var(--ink-700, #4b5563)' }}>
                        {' '}&middot; {cls.akaType || 'a.k.a.'} <em>{cls.akaName}</em>
                      </span>
                    )}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--ink-700, #4b5563)',
                    fontFamily: 'var(--font-mono, ui-monospace, Consolas, monospace)',
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  {m.aliasId && <span>SDN Alias #{m.aliasId}</span>}
                  {m.matchRef && <span>Match Ref: {m.matchRef}</span>}
                  {m.matchType && <span>Type: {m.matchType}</span>}
                </div>
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: 'var(--ink-600, #5a6672)', marginTop: 4 }}>
            Look up the full SDN entry by Alias # at{' '}
            <a
              href="https://sanctionssearch.ofac.treas.gov/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--pcb-blue)' }}
            >
              sanctionssearch.ofac.treas.gov
            </a>{' '}
            (Treasury OFAC search) before adjudicating.
          </div>
        </div>
      )}
      <div>
        {/* Label is exactly "Run" (smoke test #2, Bill 2026-06-12); the
            in-flight state still shows progress feedback while disabled. */}
        <Button
          variant="primary"
          onClick={runScan}
          disabled={!canRun}
          title="Run the OFAC sanctions screening for this name"
        >
          {running ? 'Running…' : 'Run'}
        </Button>
      </div>
      <OfacOverrideModal
        open={overrideModalOpen}
        onClose={() => setOverrideModalOpen(false)}
        onSubmit={submitOverride}
        attachedDocs={attachedDocs}
        subjectName={fullName || undefined}
        fsrOfficerCode={user?.officerCode}
      />
    </div>
  );
}
